import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPostInfo } from "../../api";
import { buildRootTabSearch } from "../../app/navigationState";
import { resolveFishSpeciesDisplayParts } from "../../utils/fishDetailsResolver";
import { resolveFishipediaEntry } from "../../utils/fishipediaResolver";
import { getWindArrow } from "../../utils/weatherPresentation";

const POST_INFO_MAP_FOCUS_ZOOM = 13;

function formatDateDMY(dateInput) {
  if (!dateInput) return null;

  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return null;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
}

function isValidDateLike(dateInput) {
  if (!hasUsableValue(dateInput)) return false;

  const date = new Date(dateInput);
  return !Number.isNaN(date.getTime());
}

function firstValidDateLike(...dateInputs) {
  return dateInputs.find(isValidDateLike) || null;
}

function isVideoMediaUrl(mediaUrl) {
  const candidateUrl = String(mediaUrl || "")
    .split("?", 1)[0]
    .trim()
    .toLowerCase();

  return /\.(mp4|mov|avi|mkv|webm|m4v)$/i.test(candidateUrl);
}

function getVideoInfoMetadata(post) {
  if (!post || typeof post !== "object") return null;

  const mediaUrl = post.resolvedMediaUrl || post.mediaUrl || "";
  if (!isVideoMediaUrl(mediaUrl)) {
    return null;
  }

  const videoMetadata = post.videoMetadata;
  return videoMetadata && typeof videoMetadata === "object"
    ? videoMetadata
    : null;
}

function formatUtcTimeHM(dateInput) {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function extractCapturedAtDisplayParts(capturedAtInput) {
  if (typeof capturedAtInput !== "string") return null;

  const normalized = capturedAtInput.trim();
  if (!normalized) return null;

  const match = normalized.match(
    /^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/i
  );

  if (!match) {
    return {
      date: formatDateDMY(normalized),
      time: null,
      utcTime: null,
    };
  }

  const [, isoDate, isoTime] = match;
  const utcTime = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
    ? formatUtcTimeHM(normalized)
    : null;

  return {
    date: formatDateDMY(isoDate),
    time: isoTime || null,
    utcTime: utcTime && utcTime !== isoTime ? utcTime : null,
  };
}

function formatVideoDuration(durationInput) {
  const durationSeconds = Number(durationInput);
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
    return null;
  }

  const totalSeconds = Math.round(durationSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatVideoDevice(deviceInput) {
  if (!deviceInput || typeof deviceInput !== "object") {
    return null;
  }

  const make = typeof deviceInput.make === "string"
    ? deviceInput.make.trim()
    : "";
  const model = typeof deviceInput.model === "string"
    ? deviceInput.model.trim()
    : "";

  if (!make && !model) {
    return null;
  }

  if (make && model) {
    const makeLower = make.toLowerCase();
    const modelLower = model.toLowerCase();

    if (modelLower.includes(makeLower)) {
      return model;
    }

    if (makeLower.includes(modelLower)) {
      return make;
    }
  }

  return [make, model].filter(Boolean).join(" ");
}

function hasUsableValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

function formatRounded(value, decimals = 1) {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const rounded = Number(value.toFixed(decimals));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function parseSizeValue(sizeInput) {
  if (!hasUsableValue(sizeInput)) return null;

  const normalized = String(sizeInput).trim().toLowerCase();
  const feetMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*(ft|foot|feet|')/i);
  const inchesMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*(in|inch|inches|")/i);

  if (feetMatch || inchesMatch) {
    const feet = feetMatch ? parseFloat(feetMatch[1]) : 0;
    const inches = inchesMatch ? parseFloat(inchesMatch[1]) : 0;
    const totalInches = feet * 12 + inches;

    if (!Number.isNaN(totalInches) && totalInches > 0) {
      return {
        cm: totalInches * 2.54,
        totalInches,
      };
    }
  }

  const numberMatch = normalized.match(/-?\d+(?:\.\d+)?/);
  if (!numberMatch) return null;

  const cm = parseFloat(numberMatch[0]);
  if (Number.isNaN(cm) || cm <= 0) return null;

  return {
    cm,
    totalInches: cm / 2.54,
  };
}

function formatImperialSize(totalInches) {
  if (typeof totalInches !== "number" || Number.isNaN(totalInches)) return null;

  const roundedInches = Math.round(totalInches);
  if (roundedInches >= 36) {
    const feet = Math.floor(roundedInches / 12);
    const inches = roundedInches % 12;
    return inches ? `${feet} ft ${inches} in` : `${feet} ft`;
  }

  return `${roundedInches} in`;
}

function formatSizeDisplay(sizeInput) {
  if (!hasUsableValue(sizeInput)) return null;

  const parsed = parseSizeValue(sizeInput);
  if (!parsed) return String(sizeInput).trim();

  const metric = `${Math.round(parsed.cm)} cm`;
  const imperial = formatImperialSize(parsed.totalInches);
  return imperial ? `${metric} (${imperial})` : metric;
}

function parseWeightValue(weightInput) {
  if (!hasUsableValue(weightInput)) return null;

  const normalized = String(weightInput).trim().toLowerCase();
  const lbMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*(lb|lbs|pound|pounds)\b/i);

  if (lbMatch) {
    const lb = parseFloat(lbMatch[1]);
    if (!Number.isNaN(lb) && lb > 0) {
      return {
        kg: lb / 2.2046226218,
        lb,
      };
    }
  }

  const numberMatch = normalized.match(/-?\d+(?:\.\d+)?/);
  if (!numberMatch) return null;

  const kg = parseFloat(numberMatch[0]);
  if (Number.isNaN(kg) || kg <= 0) return null;

  return {
    kg,
    lb: kg * 2.2046226218,
  };
}

function formatWeightDisplay(weightInput) {
  if (!hasUsableValue(weightInput)) return null;

  const parsed = parseWeightValue(weightInput);
  if (!parsed) return String(weightInput).trim();

  return `${formatRounded(parsed.kg)} kg (${formatRounded(parsed.lb)} lb)`;
}

export function calculateLocationRating(posts, coordinates) {
  if (!coordinates) return null;

  const numsCenter = coordinates.match(/(-?\d+(\.\d+)?)/g);
  if (!numsCenter || numsCenter.length < 2) return null;

  const lat = parseFloat(numsCenter[0]);
  const lng = parseFloat(numsCenter[1]);
  if (isNaN(lat) || isNaN(lng)) return null;

  const group = posts.filter((p) => {
    const d = p.catch || p;
    if (!d.coordinates) return false;

    const nums = d.coordinates.match(/(-?\d+(\.\d+)?)/g);
    if (!nums || nums.length < 2) return false;

    const lt = parseFloat(nums[0]);
    const lg = parseFloat(nums[1]);

    return Math.abs(lt - lat) < 0.002 && Math.abs(lg - lng) < 0.002;
  });

  const rated = group.filter((p) => {
    const d = p.catch || p;
    return typeof d.rating === "number" && d.rating > 0;
  });

  if (!rated.length) return null;

  const sum = rated.reduce(
    (acc, p) => acc + (p.catch?.rating || p.rating),
    0
  );

  const avg = Math.round((sum / rated.length) * 10) / 10;

  return { avg, count: rated.length, posts: rated };
}

export default function useInfoPopupView({
  post,
  posts = [],
  onClose,
  onNavigateAway,
  hideRating = false,
  hideLocationButton = false,
  viewer,
  enableFishipediaFishLink = false,
  enableVerticalScroll = false
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const data = { ...(post?.catch || post) };
  const rawFishSpeciesName =
    typeof data.fish === "string" ? data.fish.trim() : "";

  const ownerId = post?.userId || post?.sharedPost?.userId;
  const ownerUsername = post?.user || post?.sharedPost?.user;

  const viewerId = viewer?.id;
  const viewerUsername = viewer?.username;

  const isPrivateLocation =
    data?.isLocationPublic === false &&
    (
      (ownerId && viewerId && ownerId !== viewerId) ||
      (!ownerId && ownerUsername && viewerUsername && ownerUsername !== viewerUsername)
    );

  const [historicalContext, setHistoricalContext] = useState(null);
  const [isHistoricalContextLoading, setIsHistoricalContextLoading] = useState(false);
  const [showHistoricalContextLoading, setShowHistoricalContextLoading] = useState(false);
  const [fishipediaNotice, setFishipediaNotice] = useState(null);
  const [fishSpeciesDisplayName, setFishSpeciesDisplayName] = useState(
    rawFishSpeciesName
  );
  const historicalContextKeyRef = useRef("");
  const postId = post?.id || "";
  const postDate = post?.postDate || "";
  const postCoordinates = post?.catch?.coordinates || post?.coordinates || "";
  const postCatchDate = post?.catch?.date || post?.date || "";
  const postCatchTime = post?.catch?.time || post?.time || "";

  const historicalContextKey = [
    postId,
    postDate,
    postCoordinates,
    postCatchDate,
    postCatchTime,
  ].join("|");

  useEffect(() => {
    if (historicalContextKeyRef.current === historicalContextKey) return;
    historicalContextKeyRef.current = "";
    setHistoricalContext(null);
  }, [historicalContextKey]);

  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      if (!postId || !postCoordinates) return;

      const hasCurrentHistoricalContext =
        historicalContextKeyRef.current === historicalContextKey &&
        historicalContext &&
        Object.keys(historicalContext).length > 0;

      if (hasCurrentHistoricalContext) {
        if (!cancelled) {
          setIsHistoricalContextLoading(false);
        }
        return;
      }

      const nums = postCoordinates.match(/(-?\d+(\.\d+)?)/g);
      if (!nums || nums.length < 2) return;
      const lat = parseFloat(nums[0]);
      const lng = parseFloat(nums[1]);

      const parsedPostDate = Number(postDate);
      const timestamp = Number.isFinite(parsedPostDate)
        ? parsedPostDate
        : Date.now();

      if (!cancelled) {
        setIsHistoricalContextLoading(true);
      }

      try {
        const result = await getPostInfo(postId, lat, lng, timestamp);

        if (!cancelled && result) {
          historicalContextKeyRef.current = historicalContextKey;
          setHistoricalContext(result);
        }
      } finally {
        if (!cancelled) {
          setIsHistoricalContextLoading(false);
        }
      }
    }

    loadContext();

    return () => {
      cancelled = true;
    };
  }, [
    historicalContext,
    historicalContextKey,
    postCatchDate,
    postCatchTime,
    postCoordinates,
    postDate,
    postId,
  ]);

  useEffect(() => {
    if (!isHistoricalContextLoading) {
      setShowHistoricalContextLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowHistoricalContextLoading(true);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isHistoricalContextLoading]);

  useEffect(() => {
    if (!fishipediaNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFishipediaNotice(null);
    }, 3200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fishipediaNotice]);

  useEffect(() => {
    let cancelled = false;

    if (!rawFishSpeciesName) {
      setFishSpeciesDisplayName("");
      return undefined;
    }

    setFishSpeciesDisplayName(rawFishSpeciesName);

    resolveFishSpeciesDisplayParts(rawFishSpeciesName, {
      waterType: data.waterType,
    })
      .then((speciesDisplay) => {
        if (!cancelled) {
          setFishSpeciesDisplayName(
            speciesDisplay?.displayName || rawFishSpeciesName
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFishSpeciesDisplayName(rawFishSpeciesName);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data.waterType, rawFishSpeciesName]);

  const effective = {
    ...data,
    ...(data.historicalContext || {}),
    ...(historicalContext || {})
  };

  const locationLabel =
    !isPrivateLocation &&
    data.coordinates &&
    typeof effective.locationLabel === "string" &&
    effective.locationLabel.trim()
      ? effective.locationLabel.trim()
      : "";

  const windData =
    effective.windData && typeof effective.windData === "object"
      ? effective.windData
      : null;

  const videoMetadata = getVideoInfoMetadata(post);
  const videoCapturedAt = videoMetadata
    ? extractCapturedAtDisplayParts(videoMetadata.capturedAt)
    : null;
  const hasVideoCapturedAt =
    Boolean(videoCapturedAt?.date) || Boolean(videoCapturedAt?.time);

  const catchDisplayDateRaw = firstValidDateLike(
    effective.date,
    effective.time?.date,
    post?.catch?.date,
    data?.date
  );

  const displayDate = hasVideoCapturedAt
    ? videoCapturedAt?.date || formatDateDMY(catchDisplayDateRaw)
    : formatDateDMY(catchDisplayDateRaw);

  const displayTime =
    hasVideoCapturedAt
      ? videoCapturedAt?.time || null
      : typeof effective.time === "string"
        ? effective.time
        : effective.time?.local
          ? effective.time.local
          : effective.time?.utc
            ? effective.time.utc.slice(11, 16)
            : data?.time || null;

  const displayTimeUTC =
    hasVideoCapturedAt
      ? videoCapturedAt?.utcTime || null
      : effective.time && typeof effective.time === "object" && effective.time.utc
        ? effective.time.utc.slice(11, 16)
        : null;

  const videoDuration = videoMetadata
    ? formatVideoDuration(videoMetadata.durationSeconds)
    : null;
  const videoDevice = videoMetadata
    ? formatVideoDevice(videoMetadata.device)
    : null;

  const weatherValue = hasUsableValue(effective.weather)
    ? effective.weather
    : null;

  const windTextFromObject = windData
    ? [
        hasUsableValue(windData.speed) ? `${windData.speed} km/h` : null,
        hasUsableValue(windData.direction) ? windData.direction : null
      ]
        .filter(Boolean)
        .join(" ")
        .trim()
    : "";

  const windArrow =
    windData && typeof windData.deg === "number"
      ? getWindArrow(
          windData.deg,
          t("weather_wind_direction_aria", {
            defaultValue: "Wind direction {{degrees}} degrees",
            degrees: windData.deg,
          })
        )
      : null;

  const hasStructuredWind = Boolean(windTextFromObject || windArrow);

  const windValue = !hasStructuredWind && (
    typeof effective.wind === "string" && effective.wind.trim()
      ? effective.wind.trim()
      : null
  );

  const waterTempValue = hasUsableValue(effective.waterTemp)
    ? effective.waterTemp
    : null;

  const moonPhase = hasUsableValue(effective.moon?.phase)
    ? effective.moon.phase.trim()
    : "";
  const moonIllumination =
    typeof effective.moon?.illumination === "number"
      ? Math.round(effective.moon.illumination)
      : null;
  const moonValue = [moonPhase, moonIllumination !== null ? `(${moonIllumination}%)` : null]
    .filter(Boolean)
    .join(" ")
    .trim();

  const sunriseLocal = hasUsableValue(effective.sun?.sunrise?.local)
    ? effective.sun.sunrise.local
    : null;
  const sunriseUtc = hasUsableValue(effective.sun?.sunrise?.utc)
    ? effective.sun.sunrise.utc
    : null;
  const sunsetLocal = hasUsableValue(effective.sun?.sunset?.local)
    ? effective.sun.sunset.local
    : null;
  const sunsetUtc = hasUsableValue(effective.sun?.sunset?.utc)
    ? effective.sun.sunset.utc
    : null;
  const sunRelation = hasUsableValue(effective.sun?.relation)
    ? effective.sun.relation
    : null;

  const tideType = hasUsableValue(effective.tides?.type)
    ? effective.tides.type
    : null;
  const tideHeight = hasUsableValue(effective.tides?.height)
    ? effective.tides.height
    : null;

  const displaySize = formatSizeDisplay(data.size);
  const displayWeight = formatWeightDisplay(data.weight);

  if (displaySize) {
    data.size = displaySize;
  }

  if (displayWeight) {
    data.weight = displayWeight;
  }

  const buildFishingMethod = () => {
    const way = effective.wayOfFishing || "";
    const type = effective.typeOfFishing || "";
    const style = effective.style || "";

    if (!way && !type && !style) return null;

    const typeClean = type.replace(/\s*\(.*?\)\s*/g, "").trim().toLowerCase();
    const styleLower = style.toLowerCase();

    const typeWords = typeClean.split(/\s+/).filter((w) => w.length > 3);
    const typeInStyle = typeWords.some((word) => styleLower.includes(word));

    if (typeInStyle) return `${way} ${style}`.trim();
    return `${way} ${type} ${style}`.trim();
  };

  const fishingMethod = buildFishingMethod();

  const buildLureInfo = () => {
    const lure = effective.lure || "";
    const size = effective.lureSize || "";
    const color = effective.lureColor || "";
    if (!lure && !size && !color) return null;
    return `${lure} ${size} ${color}`.trim();
  };

  const lureInfo = buildLureInfo();

  const buildHookInfo = () => {
    const brand = effective.hookBrand || "";
    const type = effective.hookType || "";
    const size = effective.hookSize || "";
    if (!brand && !type && !size) return null;
    return `${brand} ${type} Size ${size}`.trim();
  };

  const hookInfo = buildHookInfo();

  const isLureFishing = () => {
    const type = (effective.typeOfFishing || "").toLowerCase();
    return ["spinning", "jigging", "popping", "fly fishing"].some((t) =>
      type.includes(t)
    );
  };

  const usesLure = isLureFishing();

  const locationStats = isPrivateLocation
    ? null
    : calculateLocationRating(posts, data.coordinates);

  const handleNavigateAway = (destination) => {
    if (typeof onNavigateAway === "function") {
      onNavigateAway(destination);
      return;
    }

    onClose?.();

    if (destination?.type === "map") {
      const { lat, lng } = destination;
      const pendingLocation = { lat, lng };

      if (Number.isFinite(destination.zoom)) {
        pendingLocation.zoom = destination.zoom;
      }

      if (destination.openOverlay === false) {
        pendingLocation.openOverlay = false;
      }

      try {
        sessionStorage.setItem(
          "pendingMapLocation",
          JSON.stringify(pendingLocation)
        );
      } catch (error) {
        console.warn("Unable to persist pending map location", error);
      }

      navigate({
        pathname: "/",
        search: buildRootTabSearch("", "map"),
        hash: "",
      });
      return;
    }

    if (destination?.type === "fishipedia" && destination.slug) {
      navigate(`/fishipedia#${destination.slug}`);
    }
  };

  const openMapAtLocation = () => {
    if (!data.coordinates) return;

    const nums = data.coordinates.match(/(-?\d+(\.\d+)?)/g);
    if (!nums || nums.length < 2) return;

    const lat = parseFloat(nums[0]);
    const lng = parseFloat(nums[1]);

    if (isNaN(lat) || isNaN(lng)) return;

    handleNavigateAway({
      type: "map",
      lat,
      lng,
      zoom: POST_INFO_MAP_FOCUS_ZOOM,
      openOverlay: false,
    });
  };

  const openFishipediaForFish = () => {
    if (!data.fish) {
      return;
    }

    const fishipediaEntry = resolveFishipediaEntry(data.fish);

    if (!fishipediaEntry?.slug) {
      setFishipediaNotice({
        id: Date.now(),
        message: t("fish_fishipedia_unavailable", {
          defaultValue:
            "This species is not yet available in Fishipedia. We update the library regularly and it will be added soon.",
        }),
      });
      return;
    }

    handleNavigateAway({
      type: "fishipedia",
      slug: fishipediaEntry.slug,
    });
  };

  return {
    t,
    post,
    onClose,
    data,
    effective,
    hideRating,
    hideLocationButton,
    enableFishipediaFishLink,
    enableVerticalScroll,
    isHistoricalContextLoading: showHistoricalContextLoading,
    displayDate,
    displayTime,
    displayTimeUTC,
    videoDuration,
    videoDevice,
    weatherValue,
    weatherCondition: effective.weatherCondition,
    hasStructuredWind,
    windTextFromObject,
    windArrow,
    windValue,
    waterTempValue,
    moonPhase,
    moonValue,
    sunriseLocal,
    sunriseUtc,
    sunsetLocal,
    sunsetUtc,
    sunRelation,
    tideType,
    tideHeight,
    locationLabel,
    isPrivateLocation,
    fishipediaNotice,
    fishSpeciesDisplayName,
    fishingMethod,
    lureInfo,
    hookInfo,
    usesLure,
    locationStats,
    openMapAtLocation,
    openFishipediaForFish,
  };
}
